// Root
export const rootRoute = '/';

// Login
export const loginRoute = '/login';
export const newPassword = '/newPassword';
export const resetPasswordRoute = '/resetPassword';
export const emailSentRoute = '/emailSent';
export const notFound = '/notFound';
export const adLibrary = 'https://content.demand-iq.com';
export const knowledgeBase = 'https://demand-iq.notion.site/Demand-IQ-Knowledge-Base-b69a0c71f7af4c3bac632b980363f9f9';
export const integrations = '/integrations';
export const getIntegrationEventsRoute = (type: string) => `/integrations/${type}/events`;
export const productAnalyticsDashboard = '/performance';
// Hero routes
export type SettingsTab = 'solar' | 'roof' | 'system' | 'hvac';
export type LocationSettingTab = SettingsTab | 'details';
export const heroSettings = '/hero/settings';
export const heroMembers = `${heroSettings}/members`;
export const heroTeams = `${heroSettings}/teams`;
export const settings = `/online-estimator/settings`;
export const heroTerritories = `${settings}/details/locations`;
export const roofSettings = `${settings}/roof`;
export const getSettingsTabRoute = (tab: 'solar' | 'roof' | 'hvac' | 'system') => {
  return `${settings}/${tab}`;
};
export const defaultSettings = (tab: SettingsTab) => {
  return `${settings}/${tab}`;
};
export const getLocationsSettingRoute = (id?: string, activeTab = 'details', showTeamModal?: boolean) => {
  const path = `${settings}/${activeTab}/locations/${id ?? 'new'}`;
  if (showTeamModal) {
    return `${path}?createTeam=true`;
  }
  return path;
};
export const locationDetails = (id?: string) => `${getLocationsSettingRoute(id)}`;
export const locationTeams = (id?: string, showModal?: boolean) => {
  const path = `${getLocationsSettingRoute(id)}/teams`;
  if (showModal) {
    return `${path}?addTeam=true`;
  }
  return path;
};
export const locationCalculatorSettings = (id?: string) => `${getLocationsSettingRoute(id)}/calculator/settings`;
export const heroBusinessSettings = `${heroSettings}/default`;
export const heroSettingsLocations = `${heroSettings}/locations`;
export const getHeroLocationSettingRoute = (id?: string) => `${heroSettings}/locations/${id ?? 'new'}`;
export const heroProspects = '/hero/prospects';
/** The Leads page with one lead's detail drawer open (`Prospects.tsx` reads `?prospectId=`). */
export const getProspectDetailRoute = (prospectId: string) =>
  `${heroProspects}?prospectId=${encodeURIComponent(prospectId)}`;
export const heroInsights = '/hero/insights';

// Workflow routes
export const workflowLanding = '/web-leads';

/** Route for folder detail view (journey list inside a folder). */
export const getWorkflowFolderDetailRoute = (folderId: string) => `${workflowLanding}/folder/${folderId}`;

export const plansLanding = '/admin/plans';
export const planListing = '/admin/plans/';

export type GetWorkflowStepRouteProps = Readonly<{
  workflowId: string;
  /**
   * The step's URL segment: its trackingId (stable across draft/publish), or its db id
   * when the trackingId is ambiguous — cloned steps share their source's trackingId.
   */
  stepTrackingId?: string;
  draftId?: string | null;
  tab?: string;
}>;

export const getWorkflowWithStepRoute = ({
  workflowId,
  stepTrackingId,
  draftId,
  tab = 'build',
}: GetWorkflowStepRouteProps) => {
  const base = workflowLanding;

  let workflowRoute = `${base}/${workflowId}`;
  if (tab === 'build') {
    workflowRoute = `${base}/${workflowId}/step`;
    if (stepTrackingId) {
      workflowRoute = `${workflowRoute}/${stepTrackingId}`;
    }
  } else if (tab === 'share') {
    workflowRoute = `${workflowRoute}/share`;
  } else if (tab === 'tracking') {
    workflowRoute = `${workflowRoute}/tracking`;
  } else if (tab === 'theme') {
    workflowRoute = `${workflowRoute}/theme`;
  } else if (tab === 'analytics') {
    workflowRoute = `${workflowRoute}/analytics`;
  } else if (tab === 'sendEmail') {
    workflowRoute = `${workflowRoute}/send-email`;
  } else if (tab === 'sendEmailHistory') {
    workflowRoute = `${workflowRoute}/send-email/history`;
  }

  //Draft Id Look up
  if (draftId) {
    return `${workflowRoute}?draft=${draftId}`;
  }

  return workflowRoute;
};
// Match whole path segments, not substrings — the /step/:stepTrackingId segment is an
// opaque id whose format the frontend doesn't control.
export const getTabForWorkflowPath = (pathname: string = document.location.pathname) => {
  const segments = pathname.split('/');
  let tab = 'build';
  if (segments.includes('send-email')) {
    tab = 'sendEmail';
  } else if (segments.includes('share')) {
    tab = 'share';
  } else if (segments.includes('tracking')) {
    tab = 'tracking';
  } else if (segments.includes('theme')) {
    tab = 'theme';
  } else if (segments.includes('analytics')) {
    tab = 'analytics';
  }
  return tab;
};

/** The step segment following /step in a builder path, if any. */
export const getStepSegmentFromPath = (pathname: string): string | undefined => {
  const segments = pathname.split('/');
  const stepIndex = segments.indexOf('step');
  if (stepIndex === -1) {
    return undefined;
  }
  return segments[stepIndex + 1] || undefined;
};

// Company routes
export const companyDetails = '/company/settings/details';
export const companyApiKeys = '/company/settings/apiKeys';
export const companyPlan = '/company/settings/plan';
export const companyWebhooksIncoming = '/company/settings/webhooks/incoming';
export const companyWebhooksOutgoing = '/company/settings/webhooks/outgoing';
export const companyListing = '/companies';
export const companySubscriptionsRoute = (id: string) => {
  return `/companies/${id}/subscriptions`;
};

// Organization routes
export const organizationListing = '/organizations';

// Profile routes
export const profileDetails = '/profile/details';
export const profileChangePassword = '/profile/changePassword';

export const marketingResources = '/marketing';

export const presentations = '/presentations';
/**
 * Default child segment under /presentations. The bare-index redirect (App.tsx), the iframe
 * defaultPath (Presentations.tsx), and the sidebar's My Presentations link must all agree,
 * or the redirect stops matching the highlighted nav item.
 */
export const presentationsDefaultPath = 'my-presentations';
/**
 * The Email Template tab of a deck's editor in the presentations app. Reminders are set
 * there, not on the journey (ENG-1517). SsoIframeShell forwards the query string.
 */
export const getDeckEmailTemplateRoute = (deckId: string): string =>
  `${presentations}/templates/${deckId}/edit?tab=delivery`;

// Dashboard
export const dashboard = '/dashboard';

// Ace routes
export const aceComingSoon = '/ace/coming-soon';
export const aceToday = '/ace/today';
export const aceConversations = '/ace/conversations';
export const aceAutoFollowUp = '/ace/auto-follow-up';
export const aceTeach = '/ace/teach';
export const aceSettings = '/ace/settings';

// Mia routes
export const miaAdCopywriter = '/mia/ad-copywriter';
export const miaToday = '/mia/today';
export const miaMetaAds = '/mia/meta-ads';
export const miaVideoAds = '/mia/video-ads';
export const miaVideoAdsSuccess = '/mia/video-ads/success';
export const miaCanonryDashboard = '/mia/canonry-dashboard';
export const miaGoogleAiSearch = '/mia/google-ai-search';
export const miaTeach = '/mia/teach';
export const miaSettings = '/mia/settings';

// Online Estimator routes
export const onlineEstimatorSettings = '/online-estimator/settings';

// Canvassing App routes
export const canvassingSettings = '/hero/settings';

// Calendar
export const calendar = '/calendar';
export const calendarLocations = `${calendar}/locations`;
export const getCalendarLocationRoute = (id?: string) => `${calendar}/locations/${id ?? 'new'}`;

type LandingNames = 'teams' | 'members' | 'company' | 'workflows';
export const landingRoutes = new Map<LandingNames, string>();
landingRoutes.set('teams', heroTeams);
landingRoutes.set('members', heroMembers);
landingRoutes.set('company', companyDetails);
landingRoutes.set('workflows', workflowLanding);

/**
 * Gets the route for single concept details.
 * @param landingKey key to look up landing page
 * @param id id of item to drill down to
 * @returns
 */
export const getSingleItemRouteForLanding = (landingKey: LandingNames, id: string): string =>
  `${landingRoutes.get(landingKey)}/${id}`;

/**
 * The page a user lands on when all we know is which app they hold (ENG-1169). Each one sits
 * behind that app's own `RequireApp` gate in App.tsx, so it is only ever handed to someone who
 * holds the app -- a landing that can bounce is a landing that can loop.
 *
 * `useDefaultLandingRoute` owns which of these wins when a user holds several.
 */
export const journeysDefaultLanding = workflowLanding;
export const presentationsDefaultLanding = `${presentations}/${presentationsDefaultPath}`;
export const heroDefaultLanding = heroInsights;

/**
 * Where a user holding none of the three belongs. Leads is ungated by design -- leads arrive from
 * Journeys and canvassing alike -- so it is the one page that can never dead-end into
 * `AppNotAvailable`, which makes it the only safe answer when entitlement is unknown or empty.
 */
export const ungatedDefaultLanding = heroProspects;

/**
 * Returns the app root. The index route (`DefaultLandingRedirect`) resolves it to the first
 * destination the user is entitled to, once company and user apps have loaded.
 *
 * Callers that switch companies cannot resolve a landing page themselves -- the new tenant's
 * apps have not been fetched yet -- so they hand off to the index instead of guessing.
 */
export const getDefaultLandingPage = () => rootRoute;
